import { useRef, useState } from 'react'
import cn from 'classnames'
import s from './TextEditor.scss'
import { makePostRequest, getBaseUrl, makeGetRequest } from 'utils/api'
import { InputContainer } from 'components/ideal-core-ui'
import { Button } from 'reactstrap'
import { Editor } from '@tinymce/tinymce-react'

const TextEditor = props => {
  const editorRef = useRef(null)
  const [title, setTitle] = useState(props.title || '')
  const [subject, setSubject] = useState(props.subject || '')

  const uploadImage = async (formData, resolve) => {
    const key = await makePostRequest('/v1/s3/uploadImage', formData)
    const src = getBaseUrl(`/v1/s3/get-s3-object/${key}`)

    resolve(src)
  }

  const imageUpload = blobInfo =>
    new Promise(resolve => {
      const formData = new FormData()
      formData.append('file', blobInfo.blob())
      uploadImage(formData, resolve)
    })

  const getToolbarItems = () => {
    let items =
      'undo redo | signaturesnippet | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor removeformat | pagebreak | charmap emoticons | fullscreen image preview | insertfile link | ltr rtl'
    if (props.hiddenTools) {
      props.hiddenTools.forEach(item => {
        items = items.replace(item, '')
      })
    }
    return items
  }

  const insertSignature = async editor => {
    const signature = await makeGetRequest('/v1/users/signature')
    editor.insertContent(signature)
  }

  return (
    <div className={cn(s.textEditorWrapper, props.className)}>
      {props.hasTitle && (
        <div>
          <span>Template Name:</span>
          <InputContainer
            type="text"
            style={{
              display: 'inline',
              marginRight: '20px',
              position: 'relative',
              bottom: '5px',
              marginLeft: '20px'
            }}
            cb={value => {
              setTitle(value)
            }}
            text={title}
            classname={s.title}
          />
        </div>
      )}
      {props.hasSubject && (
        <div>
          <span>Email Subject Line:</span>
          <InputContainer
            type="text"
            style={{ display: 'inline', marginRight: '20px', position: 'relative', bottom: '5px' }}
            cb={value => {
              setSubject(value)
            }}
            text={subject}
            classname={s.title}
          />
        </div>
      )}
      <Editor
        body_id={props.id}
        apiKey={process.env.TINYMCE_KEY}
        onInit={(evt, editor) => (editorRef.current = editor)}
        value={props.onChange ? props.template : undefined}
        initialValue={props.onChange ? undefined : props.template}
        onEditorChange={props.onChange}
        init={{
          branding: false,
          autosave_interval: props.saveInterval,
          font_size_formats:
            '8px 9px 10px 11px 12px 13px 14px 15px 16px 18px 20px 21px 24px 26px 28px 30px 32px 34px 36px 40px 48px 54px 60px 70px 80px',
          extended_valid_elements: 'snippet',
          height: props.height || 500,
          setup: function (editor) {
            editor.on('NodeChange', function (e) {
              if (e.element?.firstElementChild?.tagName === 'IMG') {
                e.element.firstElementChild.setAttribute('draggable', false)
              }
              if (e.element?.tagName === 'P') {
                e.element.setAttribute('style', 'margin: 0 !important')
              }
              if (e.element?.parentElement.tagName === 'P') {
                e.element.parentElement.setAttribute('style', 'margin: 0 !important')
              }
              if (e.element?.tagName === 'LI') {
                e.element.parentElement.setAttribute('style', 'margin: 0 !important')
              }
              if (e.element?.parentElement.tagName === 'LI') {
                e.element.parentElement.parentElement.setAttribute('style', 'margin: 0 !important')
              }
            })
            editor.ui.registry.addButton('signaturesnippet', {
              icon: 'line',
              tooltip: 'Insert Signature',
              onAction: () => {
                insertSignature(editor)
              }
            })
          },
          images_upload_handler: imageUpload,
          menubar: 'file edit view insert format tools table help',
          plugins:
            'preview importcss searchreplace autolink save directionality code visualblocks visualchars fullscreen image link table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help charmap quickbars emoticons',
          toolbar: getToolbarItems(),
          editimage_cors_hosts: ['web.idealcrm.app', 'dev.idealcrm.app'],
          font_family_formats: `Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; ${
            props.customFonts
              ? 'Poppins=poppins,sans-serif; Roboto=roboto,sans-serif; Roboto Condensed=roboto condensed,sans-serif;'
              : ''
          } Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; ${
            props.customFonts ? 'Ubuntu=ubuntu,sans-serif;' : ''
          } Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats`,
          content_style:
            props.customFonts &&
            "@import url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,400;0,700;1,400;1,700&family=Roboto+Condensed:ital,wght@0,400;0,700;1,400;1,700&family=Roboto:ital,wght@0,400;0,700;1,400;1,700&family=Ubuntu:ital,wght@0,400;0,700;1,400;1,700&display=swap');"
        }}
      />
      {props.actions && (
        <div className={s.actionBtnsWrapper}>
          {props.actions.map((button, index) => {
            return (
              <Button
                key={index}
                className={cn({
                  [button.className]: button.className
                })}
                onClick={() => {
                  button.cb(String(editorRef.current.getContent()), title, subject, () =>
                    editorRef.current.off('NodeChange')
                  )
                }}
                disabled={button.enabled !== undefined ? !button.enabled : false}
              >
                {button.text}
              </Button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TextEditor
